import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useLogout, useSession } from '../auth/session';
import { useHealth, usePool } from '../queries';
import { Badge, Button, cx, Spinner } from '../ui';

const NAV = [
  { to: '/', label: 'Match desk', end: true },
  { to: '/players', label: 'Players' },
  { to: '/teams', label: 'Teams' },
  { to: '/dota-teams', label: 'Dota teams' },
  { to: '/history', label: 'History' },
  { to: '/pool', label: 'Pool' },
  { to: '/health', label: 'Health' },
  { to: '/settings', label: 'Settings' },
];

function ConnectionPill() {
  const { data: healthData, isPending, isError } = useHealth();
  const { data: poolData } = usePool();

  const connectedBots = poolData?.status_counts?.connected ?? 0;
  const busyBots = poolData?.status_counts?.busy ?? 0;
  const availableBots = connectedBots + busyBots;

  if (isPending) {
    return (
      <span className="flex items-center gap-2 text-xs text-ink-dim">
        <Spinner className="size-3" />
        Checking API
      </span>
    );
  }

  const healthy = !isError && healthData?.status === 'healthy';
  const canary = healthData?.canary;

  return (
    <span className="flex items-center gap-2">
      <Badge tone={healthy ? 'ok' : 'bad'}>
        <span
          aria-hidden="true"
          className={cx('size-1.5 rounded-full', healthy ? 'bg-ok' : 'bg-bad')}
        />
        {healthy ? 'API healthy' : 'API unhealthy'}
      </Badge>
      {canary?.stale && <Badge tone="warn">re-checking</Badge>}
      {poolData && (
        <Badge tone="neutral">
          {availableBots}/{poolData.total_accounts} bots
        </Badge>
      )}
    </span>
  );
}

function SignOutButton() {
  const { data } = useSession();
  const logout = useLogout();

  // A client pointed at a remote API never had a session to end.
  if (data?.bypassed) return null;

  return (
    <Button size="sm" variant="ghost" loading={logout.isPending} onClick={() => logout.mutate()}>
      Sign out
    </Button>
  );
}

export default function AppShell() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 border-b border-line bg-surface-0/85 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3">
          <NavLink to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className="font-display text-lg font-semibold text-gold-300">Lobby Manager</span>
          </NavLink>

          <nav className="ml-4 hidden items-center gap-1 lg:flex">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cx(
                    'rounded-md px-3 py-1.5 text-sm transition-colors',
                    isActive
                      ? 'bg-surface-2 text-ink'
                      : 'text-ink-dim hover:bg-surface-1 hover:text-ink',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto hidden items-center gap-3 sm:flex">
            <ConnectionPill />
            <SignOutButton />
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            aria-expanded={menuOpen}
            aria-label="Toggle navigation"
            onClick={() => setMenuOpen((open) => !open)}
          >
            ☰
          </Button>
        </div>

        {menuOpen && (
          <nav className="border-t border-line px-4 pb-3 lg:hidden">
            <div className="flex flex-col py-2">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    cx(
                      'rounded-md px-3 py-2 text-sm',
                      isActive ? 'bg-surface-2 text-ink' : 'text-ink-dim hover:text-ink',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
            <div className="flex items-center justify-between pt-1 sm:hidden">
              <ConnectionPill />
              <SignOutButton />
            </div>
          </nav>
        )}
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
